<template>
  <div class="document-viewer" :style="{ width: isImage ? 'auto' : '80vw' }">
    <img v-if="isImage" ref="imageRef" :src="url" :alt="fileName" class="image-viewer"
      :class="{ 'clickable': imageExceedsContainer }" @load="checkImageDimensions"
      @click="imageExceedsContainer ? openImageInNewTab() : undefined" />
    <iframe v-else :src="url" width="100%" height="600px" frameborder="0"> </iframe>
  </div>
</template>

<script setup lang="ts">

import { messageBoxResult } from '@components/dialogs/message-box';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { useIntl } from '@services/i18n/i18nservice';
import type { DxButton } from 'devextreme-vue';
import type { ToolbarItem } from 'devextreme/ui/popup';
import { computed, getCurrentInstance, onMounted, ref } from 'vue';

const props = defineProps<{
  url: string,
  fileName: string,
  botones?: Array<{
    id: string,
    widgetOptions: Omit<DxButton, "onClick"> & { onClick: (closeFn: () => void, e: any) => void },
    itemOptions?: Omit<ToolbarItem, "widget" | "options">;
  }>
}>();
const intl = useIntl();

const imageExtensions = new Set<string>([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "tiff",
  "ico",
  "webp",
  "svg",
  "avif",
  "heic",
  "heif"
]);

const isImage = computed((): boolean => {
  const dotIndex = props.fileName.lastIndexOf(".");
  if (dotIndex === -1) return false;
  const ext = props.fileName.substring(dotIndex + 1).toLowerCase();
  return imageExtensions.has(ext);
});

const imageRef = ref<HTMLImageElement | null>(null);
const imageExceedsContainer = ref<boolean>(false);

const checkImageDimensions = () => {
  if (!imageRef.value) return;

  const img = imageRef.value;
  const naturalWidth = img.naturalWidth;
  const naturalHeight = img.naturalHeight;
  const displayedWidth = img.clientWidth;
  const displayedHeight = img.clientHeight;

  // Si la imagen se ha reducido para caber en el contenedor, permitir el clic
  imageExceedsContainer.value = naturalWidth > displayedWidth || naturalHeight > displayedHeight;
};

onMounted(() => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)

  configureButtons(modal);
});

const configureButtons = (modal: ReturnType<typeof useModalBuilder>) => {
  if (props.botones) {
    props.botones.forEach(boton => {
      if (boton.widgetOptions.onClick) {
        const originalOnClick = boton.widgetOptions.onClick;
        boton.widgetOptions.onClick = (e: any) => {
          originalOnClick(() => modal.close(messageBoxResult.ok), e);
        };
      }
      modal.addButton(boton.id, boton.widgetOptions as Omit<DxButton, "onClick">, boton.itemOptions);
    });
  }

  let okButton: ToolbarReference<DxButton>;
  let cerrar = `${intl.i18n.t("shared.ui.buttons.close")}`;
  okButton = modal.standardButtons.addOk();
  okButton.update(w => w.text = cerrar);
  okButton.update(w => w.onClick = () => modal.close(messageBoxResult.ok));
}

const openImageInNewTab = () => {
  // Crear un documento HTML que muestre la imagen a tamaño completo desde el inicio
  const htmlContent = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>${props.fileName}</title>
        <style>
          body {
            margin: 0;
            padding: 0;
            background-color: var(--background-normal-dark);
            display: flex;
            justify-content: center;
            align-items: flex-start;
            min-height: 100vh;
          }
          img {
            display: block;
            max-width: none;
            height: auto;
            image-rendering: -webkit-optimize-contrast;
          }
        </style>
      </head>
      <body>
        <img src="${props.url}" alt="${props.fileName}" />
      </body>
    </html>
  `;

  const blob = new Blob([htmlContent], { type: "text/html" });
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, "_blank");
}

</script>

<style scoped>
.document-viewer {
  border: none;
}

.image-viewer {
  height: 64dvh;
  object-fit: contain;

  &.clickable {
    cursor: zoom-in;
  }
}
</style>